import React, {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import ReviewApplicantForm from './ReviewApplicantForm'
import Resume from 'components/resume'
import Loader from 'components/preloader'
import {getSpecificApplicants} from 'api/private/applicants'
import 'style/_review.scss'

/**
 * Safely parse workExperiences field from various formats
 * Handles: JSON string, "[object Object]" string, array, or undefined
 * @param {string|array|undefined} workExperiences - The workExperiences field value
 * @returns {array} - Parsed array of work experiences or empty array
 */
const parseWorkExperiences = (workExperiences) => {
  // If already an array, return it
  if (Array.isArray(workExperiences)) {
    return workExperiences
  }

  // If undefined or null, return empty array
  if (!workExperiences) {
    return []
  }

  // If it's a string, try to parse it
  if (typeof workExperiences === 'string') {
    // Handle "[object Object]" case - this means the object was stringified incorrectly
    if (workExperiences === '[object Object]' || workExperiences.trim() === '[object Object]') {
      return []
    }

    // Try to parse as JSON
    try {
      const parsed = JSON.parse(workExperiences)
      // If parsed result is an array, return it
      if (Array.isArray(parsed)) {
        return parsed
      }
      // If parsed result is an object (single experience), wrap it in array
      if (typeof parsed === 'object' && parsed !== null) {
        return [parsed]
      }
    } catch (e) {
      // If JSON parsing fails, return empty array
      return []
    }
  }

  // Default: return empty array
  return []
}

/**
 * Transform new work experience format to old format
 * @param {object} profile - The applicant profile object
 * @returns {object} - Object with transformed work experience fields
 */
const transformWorkExperience = (profile) => {
  // Check if old format fields already exist (backward compatibility)
  const hasOldFormat = 
    profile.person_position !== undefined ||
    profile.person_startDate !== undefined ||
    profile.person_lastDate !== undefined ||
    profile.person_place_work !== undefined ||
    profile.person_reason_for_leaving !== undefined ||
    profile.fresh_graduate !== undefined

  // If old format exists, use it (backward compatibility)
  if (hasOldFormat) {
    return {
      person_position: profile.person_position || '',
      person_startDate: profile.person_startDate || '',
      person_lastDate: profile.person_lastDate || '',
      person_place_work: profile.person_place_work || '',
      person_reason_for_leaving: profile.person_reason_for_leaving || '',
      fresh_graduate: profile.fresh_graduate || 'false',
    }
  }

  // Transform from new format
  const workType = profile.workType
  const workExperiencesRaw = profile.workExperiences
  const workExperiences = parseWorkExperiences(workExperiencesRaw)

  // If workType is 'without', set as fresh graduate
  if (workType === 'without') {
    return {
      person_position: '',
      person_startDate: '',
      person_lastDate: '',
      person_place_work: '',
      person_reason_for_leaving: '',
      fresh_graduate: 'true',
    }
  }

  // If workType is 'with' and we have work experiences
  if (workType === 'with' && workExperiences.length > 0) {
    const latestExperience = workExperiences[0]
    return {
      person_position: latestExperience.position || '',
      person_startDate: latestExperience.startDate || '',
      person_lastDate: latestExperience.endDate || '',
      person_place_work: latestExperience.companyName || '',
      person_reason_for_leaving: latestExperience.reasonForLeaving || '',
      fresh_graduate: 'false',
      // Pass all work experiences for display
      workExperiences: workExperiences,
    }
  }

  // If workType is 'with' but workExperiences is "[object Object]" (data lost)
  // This happens when the array was incorrectly stringified during form submission
  if (workType === 'with' && typeof workExperiencesRaw === 'string' && 
      (workExperiencesRaw === '[object Object]' || workExperiencesRaw.trim() === '[object Object]')) {
    // Data is lost, but we know they have work experience
    // Return empty fields but indicate they're not a fresh graduate
    return {
      person_position: '[Data unavailable - please contact applicant]',
      person_startDate: '',
      person_lastDate: '',
      person_place_work: '',
      person_reason_for_leaving: '',
      fresh_graduate: 'false',
    }
  }

  // Default: no work experience data
  return {
    person_position: '',
    person_startDate: '',
    person_lastDate: '',
    person_place_work: '',
    person_reason_for_leaving: '',
    fresh_graduate: 'false',
  }
}

const ReviewApplicantPage = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const {id, company} = useParams()

  const getApplicant = async () => {
    const r = await getSpecificApplicants(id, company)
    const formatData = []
    if (r) {
      const profile = JSON.parse(r[0].data)
      formatData.push({
        name: `${profile.person_fname} ${profile.person_mname} ${profile.person_lname}`,
        firstName: profile.person_fname,
        middleName: profile.person_mname,
        lastName: profile.person_lname,
        perm_addr: `${profile.person_permanent_address_street_blk_pres} ${profile.person_permanent_address_street} ${profile.person_permanent_address_zipcode} ${profile.person_permanent_address_city}`,
        pres_addr: `${profile.person_present_address_street_blk} ${profile.person_present_address_street} ${profile.person_present_address_zipcode} ${profile.person_present_address_city}`,
        profile_img: r[0].profile,
        reference_id: r[0].reference_id,
        email: profile.person_email,
        age: profile.person_age,
        birthdate: profile.person_birthdate,
        contact: profile.person_contact_no_mob,
        sex: profile.person_sex,
        civil_status: profile.person_civil_status,
        religion: profile.person_rel,
        height: profile.person_ht,
        weight: profile.person_wt,
        blood_type: profile.person_bloodtype,
        tin: profile.person_tin,
        sss: profile.person_sss,
        phl: profile.person_phlhealth,
        pagibig: profile.person_pagibig,
        emerg_name: profile.person_emergency_name,
        emerg_contact: profile.person_emergency_contact_no,
        emerg_relationship: profile.person_emergency_relationship,
        nationality: profile.person_nationality,
        last_menstruation_date: profile.last_menstruation_date,
        id: r[0].id,
        company: r[0].company,
        // Additional fields
        person_findHiring: profile.person_findHiring,
        invited_by: profile.invited_by,
        referral_name: profile.referral_name,
        person_landmark_address: profile.person_landmark_address,
        pref_objective: profile.pref_objective,
        pref_image: profile.pref_image,
        sworn: profile.sworn,
        // Education
        educ_elem: profile.educ_elem,
        educ_elem_year: profile.educ_elem_year,
        educ_hs: profile.educ_hs,
        educ_hs_year: profile.educ_hs_year,
        educ_jhs: profile.educ_jhs,
        educ_jhs_year: profile.educ_jhs_year,
        educ_college: profile.educ_college,
        educ_college_graduated: profile.educ_college_graduated,
        educ_college_course: profile.educ_college_course,
        educ_college_year: profile.educ_college_year,
        educ_voc: profile.educ_voc,
        educ_voc_course: profile.educ_voc_course,
        educ_voc_year: profile.educ_voc_year,
        // Family Information
        person_father: profile.person_father,
        person_father_bdate: profile.person_father_bdate,
        person_father_occ: profile.person_father_occ,
        person_mother: profile.person_mother,
        person_mother_bdate: profile.person_mother_bdate,
        person_mother_occ: profile.person_mother_occ,
        person_spouse_name: profile.person_spouse_name,
        person_spouse_occ: profile.person_spouse_occ,
        person_spouse_child: profile.person_spouse_child,
        person_spouse_age: profile.person_spouse_age,
        with_sibling: profile.with_sibling,
        with_spouse: profile.with_spouse,
        // Medical Information
        medical_agree_no_history: profile.medical_agree_no_history,
        family_agree_no_hospitalization: profile.family_agree_no_hospitalization,
        family_agree_no_history: profile.family_agree_no_history,
        no_hospitalizations: profile.no_hospitalizations,
        illness_none: profile.illness_none,
        medical_history_none: profile.medical_history_none,
        family_history_none: profile.family_history_none,
        no_surgery: profile.no_surgery,
        surgery_name: profile.surgery_name,
        surgery_hospital: profile.surgery_hospital,
        surgery_year: profile.surgery_year,
        other_hosp_name: profile.other_hosp_name,
        other_hosp_hospital: profile.other_hosp_hospital,
        other_hosp_year: profile.other_hosp_year,
        // Additional medical fields
        medical_history_dysmenorrhea: profile.medical_history_dysmenorrhea,
        menstrual_cycle: profile.menstrual_cycle,
        pcos_value: profile.pcos_value,
        ...profile,
        ...r[0],
        // Work Experience - Transform from new format if needed (after profile spread to override workExperiences string)
        ...transformWorkExperience(profile),
      })
      setLoading(false)
      setData(formatData)
    }
  }

  const handleWindowClose = e => {
    return window.close()
  }

  useEffect(() => {
    getApplicant()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className={`review_application-body ${loading ? `is-loading` : ``}`}>
      {!loading ? (
        <div className="review-container-fluid">
          <div className="row">
            <div className="col-5 form">
              {data && parseInt(data[0]?.applying_for) !== 0 ? (
                <ReviewApplicantForm details={data} />
              ) : (
                <>
                  <h3>Applicant didn't select job applying for.</h3>
                  <button class="btn btn-outline-primary" type="button" onClick={e => handleWindowClose(e)}>
                    Close tab
                  </button>
                </>
              )}
            </div>
            <div className="col-7 preview">
              <Resume details={data} />
            </div>
          </div>
        </div>
      ) : (
        <Loader />
      )}
    </div>
  )
}

export {ReviewApplicantPage}
